import axios, { AxiosError } from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { looksLikeHtml, SERVER_UNAVAILABLE_MESSAGE } from '../utils/serverDownMessage';

/**
 * Audit Wave 1 / Lane 5 (D10, M-057 partial): a single response interceptor on the global axios
 * default instance that logs the caller out on a 401 from the backend.
 *
 * Deliberately NOT a shared axios instance swap (that is 86 service files + 32 `jest.mock('axios')`
 * test files — ticketed separately as W1-L5-T2). Every service already goes through
 * `axios.defaults` (the bearer already lives there), so one interceptor on the default instance
 * covers all of them with a single, easily-revertible commit.
 *
 * Fix-round-1 (C1, task-review CRITICAL): the original spec (D10) said "fires on 401 || 403" — this
 * codebase treats 403 as its ordinary permission-denied signal, not a dead-session signal. It's the
 * status `ToastContext.showApiError` renders as "you don't have permission" (not a logout), the
 * status the free-tier seat cap answers with, and the status the dashboard fires *by design* in a
 * burst for widgets the viewer lacks keys for. Firing a hard logout+redirect on it broke every one
 * of those working screens for any non-superuser. Overruled: **401 only.** See the decision note's
 * D10 section for the recorded overrule.
 */

// Module-level flag, not URL matching: an impersonation token swap is two calls (grant fetch +
// swap), not a single distinguishable request, so suppressing by URL suffix would miss half of it.
// Set/cleared by AuthService.impersonate / AuthService.exitImpersonation around the swap.
let isSwapping = false;

export function setImpersonationSwapInProgress(value: boolean): void {
    isSwapping = value;
}

function pathOnly(url: string | undefined): string {
    if (!url) return '';
    const withoutQuery = url.split('?')[0];
    return withoutQuery;
}

// Exact-suffix match against the known auth endpoints so a 401 from a wrong password (login) or an
// expired/incorrect 2FA code (verify) never triggers a logout — those are expected, user-facing
// failures, not "your session died" signals. Order-independent since each suffix is unambiguous.
function isWhitelistedAuthUrl(url: string | undefined): boolean {
    const path = pathOnly(url);
    // Each suffix is unambiguous against the others (e.g. '/authentication/verify' does not end
    // with '/authentication'), so evaluation order here doesn't matter.
    return (
        path.endsWith(loginSuffix()) ||
        path.endsWith(verifySuffix()) ||
        path.endsWith(refreshSuffix()) ||
        path.endsWith(suffixOf(ENDPOINTS.AUTH.LOGOUT)) ||
        path.endsWith(suffixOf(ENDPOINTS.AUTH.IMPERSONATE_EXIT))
    );
}

function loginSuffix(): string {
    return suffixOf(ENDPOINTS.AUTH.LOGIN);
}

function verifySuffix(): string {
    return suffixOf(ENDPOINTS.AUTH.VERIFY_TWO_FACTOR);
}

function refreshSuffix(): string {
    return suffixOf(ENDPOINTS.AUTH.REFRESH);
}

// The endpoint constants are full URLs (e.g. `${BASE_URL}/authentication`); an axios error's
// `config.url` may be relative or absolute depending on how the call was made, so compare on the
// path suffix rather than the full string.
function suffixOf(fullUrl: string): string {
    try {
        return new URL(fullUrl).pathname;
    } catch {
        return fullUrl;
    }
}

/**
 * Registers exactly one response interceptor on the global axios instance. Returns an eject
 * function; call it on unmount so React 18 StrictMode's double-invoke of the registering effect
 * cannot stack two interceptors.
 *
 * Early-returns a no-op ejector if `axios.interceptors.response.use` is not callable — keeps this
 * module safe to import from any test that automocks axios wholesale.
 *
 * Fix-round-1 (M2, folded into the C1 revision): a `redirecting` latch scoped to this call's
 * closure — fresh on every `installAuthInterceptor` call, so a StrictMode remount gets its own
 * clean latch. A dashboard firing N parallel requests against an expired token calls
 * `onUnauthenticated` (logout + `window.location.assign`) exactly once, not N times; the latch is
 * set before `onUnauthenticated` runs so a second 401 arriving synchronously (before navigation
 * actually tears the page down) still sees it. If the callback returns `false` — it decided NOT
 * to navigate (already on `/login`) — the latch is released again, so a later session's 401 on a
 * page that was never torn down is not swallowed (FR-M1).
 */
export function installAuthInterceptor(
    onUnauthenticated: () => boolean | void,
    refresh?: () => Promise<string | null>,
): () => void {
    if (typeof axios.interceptors?.response?.use !== 'function') {
        return () => {};
    }

    let redirecting = false;

    const interceptorId = axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const status = error.response?.status;
            // A proxy error page (nginx "502 Bad Gateway" while the server restarts) is HTML; every screen
            // that shows `response.data` would paint it, so swap it for plain words here once.
            if (error.response && typeof error.response.data === 'string' && looksLikeHtml(error.response.data)) {
                error.response.data = SERVER_UNAVAILABLE_MESSAGE;
            }
            // OWASP A07: the backend refuses everything but the password change while a temporary password is in use.
            if (status === 403 && (error.response?.data as { code?: string } | undefined)?.code === 'PASSWORD_CHANGE_REQUIRED'
                && window.location.pathname !== '/change-password') {
                window.location.assign('/change-password');
            }
            // The 15-minute access token expired (or another tab rotated the session): spend the
            // refresh cookie once (shared by every request that failed together) and replay the
            // request with the new token. A request is replayed at most once.
            const config = error.config as (AxiosError['config'] & { _authRetried?: boolean }) | undefined;
            if (status === 401 && refresh && config && !config._authRetried && !isSwapping
                && !isWhitelistedAuthUrl(config.url)) {
                config._authRetried = true;
                let token: string | null = null;
                try {
                    token = await refresh();
                } catch {
                    // Network or server failure while refreshing: fall through to the 401 handling.
                }
                if (token) {
                    (config.headers as any)['Authorization'] = `Bearer ${token}`;
                    return axios.request(config);
                }
            }
            if (status === 401) {
                if (!redirecting && !isSwapping && !isWhitelistedAuthUrl(error.config?.url)) {
                    redirecting = true;
                    if (onUnauthenticated() === false) {
                        redirecting = false;
                    }
                }
            }
            return Promise.reject(error);
        }
    );

    return () => {
        axios.interceptors.response.eject(interceptorId);
    };
}
